<template>
  <v-dialog
    :value="value"
    max-width="880"
    @input="$emit('input', $event)"
  >
    <v-card v-if="episode">
      <v-card-title class="d-flex align-center flex-nowrap">
        <span class="text-h6 text-truncate">{{ episode.title }}</span>
        <v-spacer />
        <v-btn
          icon
          data-qa="advisor-podcast-player-close"
          @click="$emit('input', false)"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-responsive :aspect-ratio="16 / 9">
        <iframe
          v-if="value && playerUrl"
          ref="playerFrame"
          :src="playerUrl"
          class="podcast-player-frame"
          allow="autoplay; fullscreen"
          allowfullscreen
          frameborder="0"
          referrerpolicy="strict-origin-when-cross-origin"
          data-qa="advisor-podcast-player-iframe"
        />
      </v-responsive>

      <v-card-text class="pt-3">
        <div
          class="text-caption grey--text"
          data-qa="advisor-podcast-player-meta"
        >
          <template v-if="episode.published_at">
            <span>Published {{ formatDate(episode.published_at) }}</span>
            <span class="mx-1">·</span>
          </template>
          <span v-if="episode.duration_seconds">
            {{ formatVideoDuration(episode.duration_seconds) }}
          </span>
        </div>
        <p
          v-if="episode.description"
          class="text-body-2 mt-2 mb-0"
        >
          {{ episode.description }}
        </p>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { trackPodcastProgress, trackPodcastView } from '@/api/ibModule';
import { formatDate, formatVideoDuration } from '@/helpers/formatting';
import { youTubeEmbedUrl } from '@/helpers/youtube';
import {
  createYouTubePlayer,
  playerDurationSeconds,
  pollWatchTime,
} from '@/helpers/youtubePlayer';

export default {
  name: 'AdvisorPodcastPlayerDialog',

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    episode: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      player: null,
      stopPolling: null,
      domReady: false,
    };
  },

  computed: {
    playerUrl() {
      if (this.episode.youtube_video_id) {
        return youTubeEmbedUrl(this.episode.youtube_video_id);
      }
      if (this.episode.wistia_hashed_id) {
        return `https://fast.wistia.net/embed/iframe/${this.episode.wistia_hashed_id}?autoPlay=true`;
      }
      return null;
    },

    isYouTubeEpisode() {
      return Boolean(this.episode && this.episode.youtube_video_id);
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(open) {
        if (open && this.episode) {
          // Fire-and-forget: the backend decides whether the play counts
          // (owner previews and drafts never do), so failures stay silent.
          trackPodcastView({ episode_id: this.episode.id }).catch(() => {});
        }
        if (!this.domReady) return;
        if (open) this.setUpPlayer();
        else this.teardownPlayer();
      },
    },
  },

  mounted() {
    this.domReady = true;
    if (this.value) this.setUpPlayer();
  },

  destroyed() {
    this.teardownPlayer();
  },

  methods: {
    formatDate,
    formatVideoDuration,

    async setUpPlayer() {
      if (!this.isYouTubeEpisode) return;

      await this.$nextTick();
      const iframe = this.$refs.playerFrame;
      if (!iframe) return;

      try {
        this.player = await createYouTubePlayer(iframe);
      } catch (e) {
        return;
      }
      if (!this.value) {
        this.player.destroy();
        this.player = null;
        return;
      }
      this.stopPolling = pollWatchTime(this.player, this.reportProgress);
    },

    teardownPlayer() {
      if (this.stopPolling) {
        this.stopPolling();
        this.stopPolling = null;
      }
      if (!this.player) return;

      this.reportProgress(Math.trunc(this.player.getCurrentTime?.() || 0));
      this.player.destroy();
      this.player = null;
    },

    reportProgress(seconds) {
      if (!seconds || !this.episode) return;
      trackPodcastProgress({
        episode_id: this.episode.id,
        watched_seconds: seconds,
        duration_seconds: this.player ? playerDurationSeconds(this.player) : 0,
      }).catch(() => {});
    },
  },
};
</script>

<style scoped>
.podcast-player-frame {
  display: block;
  width: 100%;
  height: 100%;
}
</style>
